/**
 * The ✕ of a plan's stop, answered at once (INV-PH-114, prod QA 14.10.2026, thread c03beff0).
 *
 * The press SENDS a line to the chat — «Убери «X» из маршрута» — and the plan changes in the NEXT
 * answer, a card the model's turn brings (10–30 s). Until then nothing on the old card changed and the
 * sheet had closed, so the tester pressed again after four seconds: the client interrupted the first
 * turn (`App`'s `decideSend`, «перебивание») and sent the line a second time. (Since 18.10.2026 a send over
 * the visitor's own turn is ignored instead, INV-PH-137 — the hold below still stops the double press.)
 *
 * So the press is remembered here, outside any card: the row of that stop says «Убираю…», every ✕ of
 * the card is locked, and the widget under the chat says the same. It lets go when the turn that
 * carried the line is over — answered, failed or cancelled (`useChat.status` back to `idle` on a
 * turn other than the one the press was made in) — so a turn that did not remove the stop leaves the
 * row as it was, with its ✕ working again. The chat's own bubble with the line stays, as it did.
 *
 * **It survives a reload (F5)** — the run goes on at the server while the page does not: the hold is kept
 * in the tab's `sessionStorage` under the THREAD, with the time of the press and, once the run has an id,
 * the run's. After the thread is restored (`resumeRemoval`) it is held again only if the restored chat
 * still ends with that very line, unanswered, and the hold has not run out (`HOLD_MS`) — and, when the
 * run's id is known, the server says the run is still going. An answered line, a line that never reached
 * the thread, another thread's hold, an expired one: let go, and the ✕ work.
 *
 * A pure module over three stores: no React, no network of its own, testable on its own.
 */

import { create } from 'zustand';

import { send as sendToChat } from '../lib/send';
import { useChat } from '../store/chatStore';
import { useSession } from '../store/sessionStore';
import { getRun } from '../transport/api';

/** The line a ✕ sends — the graph's own removal wording, read by the model on `peterhof_agent`. */
export function removalLine(title: string): string {
  return `Убери «${title}» из маршрута`;
}

/** The longest a press is held for: past a turn's worst case (the 90 s grace of a deploy) nothing is
 *  waiting any more, and a ✕ that stayed locked would be worse than a second press. */
export const HOLD_MS = 120_000;

export type PendingRemoval = {
  title: string;
  /** `useChat.turn` when the press was made (or the page restored) — the turn that answers it is a later one. */
  turn: number;
  /** The thread the press was made in. */
  threadId: string;
  /** When (ms since the epoch) — the hold runs out `HOLD_MS` after it, reload or not. */
  since: number;
};

type RemovalState = {
  pending: PendingRemoval | null;
  begin: (title: string, turn: number, since?: number, threadId?: string) => void;
  release: () => void;
};

let timer: ReturnType<typeof setTimeout> | undefined;

// ── the tab's memory of a hold ───────────────────────────────────────────
type Held = { title: string; since: number; runId?: string };

const keyOf = (threadId: string) => `ph.planRemoval.${threadId}`;

function readHeld(threadId: string): Held | null {
  try {
    const raw = sessionStorage.getItem(keyOf(threadId));
    const held = raw ? (JSON.parse(raw) as Partial<Held>) : null;
    if (!held || typeof held.title !== 'string' || typeof held.since !== 'number') return null;
    return { title: held.title, since: held.since, ...(typeof held.runId === 'string' ? { runId: held.runId } : {}) };
  } catch {
    return null;
  }
}

function writeHeld(threadId: string, held: Held | null): void {
  try {
    if (held) sessionStorage.setItem(keyOf(threadId), JSON.stringify(held));
    else sessionStorage.removeItem(keyOf(threadId));
  } catch {
    // blocked storage: the hold lives in memory only, as before
  }
}

/** The run id already written beside the hold. */
let savedRun: string | null = null;

export const usePlanRemoval = create<RemovalState>((set, get) => ({
  pending: null,
  begin: (title, turn, since = Date.now(), threadId = useSession.getState().threadId) => {
    clearTimeout(timer);
    timer = setTimeout(() => usePlanRemoval.getState().release(), Math.min(HOLD_MS, Math.max(0, HOLD_MS - (Date.now() - since))));
    savedRun = null;
    writeHeld(threadId, { title, since });
    set({ pending: { title, turn, threadId, since } });
  },
  release: () => {
    clearTimeout(timer);
    timer = undefined;
    const { pending } = get();
    if (pending) writeHeld(pending.threadId, null);
    savedRun = null;
    set({ pending: null });
  },
}));

// The turn that carried the line is over: the chat is idle on a turn other than the pressed one. A new
// conversation (`useChat.reset`: no bubbles at all — a chat with a plan in it has some) lets go as well.
// And once the run has an id, it goes beside the hold, for a reload to ask the server about.
useChat.subscribe((chat) => {
  const { pending, release } = usePlanRemoval.getState();
  if (!pending) return;
  if (chat.status === 'idle' && (chat.turn !== pending.turn || chat.bubbles.length === 0)) {
    release();
    return;
  }
  if (chat.runId && chat.runId !== savedRun && chat.turn !== pending.turn) {
    savedRun = chat.runId;
    writeHeld(pending.threadId, { title: pending.title, since: pending.since, runId: chat.runId });
  }
});

/**
 * A ✕ pressed on *title*. → whether the line went out. Nothing goes out while another press waits for
 * its answer (the same row, or any other ✕ of the card): a second line would have interrupted the first turn (now it would be ignored, INV-PH-137).
 */
export function requestRemoval(title: string, send: (text: string) => boolean = sendToChat): boolean {
  if (usePlanRemoval.getState().pending) return false;
  usePlanRemoval.getState().begin(title, useChat.getState().turn);
  const taken = send(removalLine(title));
  if (!taken) usePlanRemoval.getState().release();
  return taken;
}

/** The run states that mean the server is still working on the line. */
const ACTIVE_RUN = new Set(['pending', 'running']);

/**
 * After a reload: hold the ✕ again if the restored *thread* still waits for the answer to a press made
 * before it. *bubbles* are the restored chat's. Held only when ALL of: the tab remembers a hold for this
 * thread; it has not run out; the chat's last bubble is the visitor's removal line itself (an answer after
 * it, or a line that never reached the thread, means nothing is outstanding); and — with a run id on
 * record — the server still reports that run as going (a refused or failed question keeps the hold: better a
 * ✕ locked until it runs out than a second line over a live turn).
 */
export async function resumeRemoval(
  threadId: string,
  bubbles: ReadonlyArray<{ role: string; text: string }>,
  now: number = Date.now(),
  runStatus: (threadId: string, runId: string) => Promise<{ status: string }> = getRun,
): Promise<void> {
  const held = readHeld(threadId);
  if (!held) return;
  const last = bubbles[bubbles.length - 1];
  const outstanding = last?.role === 'user' && last.text.trim() === removalLine(held.title);
  const age = now - held.since;
  if (!outstanding || age < 0 || age >= HOLD_MS) {
    writeHeld(threadId, null);
    return;
  }
  usePlanRemoval.getState().begin(held.title, useChat.getState().turn, held.since, threadId);
  if (held.runId) {
    savedRun = held.runId;
    writeHeld(threadId, held);
    try {
      const run = await runStatus(threadId, held.runId);
      const { pending } = usePlanRemoval.getState();
      if (!ACTIVE_RUN.has(run.status) && pending?.since === held.since) usePlanRemoval.getState().release();
    } catch {
      // the server could not say: keep the hold until it runs out
    }
  }
}
